<template>
  <div>
    <v-autocomplete
      v-bind="{disabled}"
      :value="selectedLocations"
      :items="locationsOptions.map(l => {return {locationName: l.name, locationId: l.id}})"
      :label="!selectedLocations.length ? 'All Locations' : 'Locations'"
      multiple
      chips
      item-text="locationName"
      item-value="locationId"
      :error-messages="locationsErrorMessages"
      :rules="[v => {return !locationsErrorMessages}]"
    >
      <template #append>
        <v-btn
          v-if="selectedLocations.length"
          text
          :disabled="disabled || !selectedLocations.length"
          :ripple="false"
          color="primary"
          @click="setAllLocations"
        >
          All Locations
        </v-btn>
      </template>
      <template #selection="{ item, index }">
        <v-chip
          v-bind="{disabled}"
          close
          close-icon="mdi-close"
          :class="{error: !selectedMealPeriods.some(e => e.locationId === item.locationId) && !updatingMealPeriods}"
          :style="{transition: 'background-color 0.2s ease-in-out'}"
          @click:close="toggleLocation(item, false)"
        >
          {{ item.locationName }}
        </v-chip>
      </template>
      <template #item="{ item, on, attrs }">
        <v-list-item
          v-bind="attrs"
          v-on="on"
          @click="toggleLocation(item, !selectedLocations.find(l => l.locationId === item.locationId))"
        >
          <v-simple-checkbox 
            :value="!!selectedLocations.find(l => l.locationId === item.locationId)"
            class="pr-2"
            color="primary"
            :ripple="false"
            @input="toggleLocation(item, $event)"
          />
          {{ item.locationName }}
        </v-list-item>
      </template>
    </v-autocomplete>
    <key-refresher
      ref="mealPeriods"
      unique-key="meal-periods"
    >
      <template v-slot="{ refreshKey }">
        <v-autocomplete
          :key="refreshKey"
          :value="selectedMealPeriods"
          :items="mealPeriodsOptions"
          label="Meal Periods"
          multiple
          chips
          deletable-chips
          item-text="mealPeriodName"
          item-value="mealPeriodId"
          :disabled="disabled"
          :loading="updatingMealPeriods"
        >
          <template #selection="{ item, index }">
            <v-chip
              v-bind="{disabled}"
              close
              close-icon="mdi-close"
              @click:close="toggleMealPeriod(item, false)"
            >
              {{ item.mealPeriodName }}
            </v-chip>
          </template>
          <template #item="{ item, on, attrs }">
            <v-list-item
              v-bind="attrs"
              v-on="on"
              @click="toggleMealPeriod(item, !selectedMealPeriods.find(m => m.mealPeriodId === item.mealPeriodId))"
            >
              <v-simple-checkbox 
                :value="!!selectedMealPeriods.find(m => m.mealPeriodId === item.mealPeriodId) || false"
                v-bind="attrs"
                class="pr-2"
                :ripple="false"
                color="primary"
                v-on="on"
                @input="toggleMealPeriod(item, $event)"
              />
              {{ item.mealPeriodName }}
            </v-list-item>
          </template>
          <template #append>
            <v-btn
              v-if="!(selectedLocations.length || selectedMealPeriods.length)"
              text
              color="primary"
              @click="setAllMealPeriods"
            >
              All Meal Periods
            </v-btn>
          </template>
        </v-autocomplete>
      </template>
    </key-refresher>
  </div>
</template>

<script>
import KeyRefresher from '@/ft-vue-core/components/helpers/KeyRefresher'
import errorHandler from '@/ft-vue-core/mixins/errorHandler'
function* getUpdateKey () {
  for (let i = 0; ; i++) yield i
}
export default {
  name: 'LocationMealPeriodLinkedInput',
  components: {KeyRefresher},
  mixins: [errorHandler],
  props: {
    serverId: Number,
    locationsOptions: {
      type: Array,
      default () {return []}
    },
    locations: {
      type: Array,
      default () {return []}
    },
    mealPeriods: {
      type: Array,
      default () {return []}
    },
    disabled: Boolean,
  },
  data () {
    return {
      mealPeriodsOptions: [],
      updateKeyGenerator: getUpdateKey(),
      updateKey: -1,
      updatingMealPeriods: false,
    }
  },
  computed: {
    selectedMealPeriodsMapByLocation () {
      return this.selectedMealPeriods?.reduce((acc, r) => {
        if (!acc[r.locationId]?.length) {
          acc[r.locationId] = new Array()
        }
        acc[r.locationId].push(r)
        return acc
      }, {}) ?? {}
    },
    mealPeriodsOptionsMap () {
      return this.mealPeriodsOptions.reduce((acc, r) => {
        acc[r.mealPeriodId] = r
        return acc
      }, {}) ?? {}
    },
    locationsOptionsMap () {
      return this.locationsOptions?.reduce((acc, r) => {
        acc[r.id] = r
        return acc
      }, {}) ?? {}
    },
    selectedMealPeriods: {
      get () {
        return this.mealPeriods ?? []
      },
      set (value) {
        let result = new Array()
        for (let m of value) {
          if (typeof m === 'number') {
            const {mealPeriodId, mealPeriodName, locationId} = this.mealPeriodsOptionsMap[m] ?? {}
            result.push({mealPeriodName, mealPeriodId, locationId})
          }
          else if (m.mealPeriodId != null) result.push(m)
          else result.push({mealPeriodId: m.id, mealPeriodName: m.name, locationId: m.locationId})
        }
        this.$emit('input:meal-periods', result)
        this.$refs.mealPeriods?.refresh()
      }
    },
    selectedLocations: {
      get () {
        this.updateKey
        return this.locations ?? []
      },
      set (value) {
        let result = new Array()
        for (let l of value) {
          if (typeof l === 'number') result.push({locationId: l, locationName: this.locationsOptionsMap[l].name})
          else if (l.locationId) result.push(l)
          else result.push({locationId: l.id, locationName: l.name})
        }
        this.$emit('input:locations', result)
        this.updateKey = this.updateKeyGenerator.next().value // Force computed property to update
      }
    },
    selectedLocationsMap () {
      return this.selectedLocations?.reduce((acc, r) => {
        acc[r.locationId] = r
        return acc
      }, {}) ?? {}
    },
    locationsErrorMessages () {
      return this.locationMealPeriodConnectionRule(this.selectedLocations)
    },
  },
  watch: {
    locations () {
      this.updateMealPeriods(() => {
        const {length: locations} = this.locations || {}
        if (locations === this.locationsOptions.length || locations === 0) {
          this.selectedMealPeriods = [...this.mealPeriodsOptions]
        }
      })
    }
  },
  mounted () {
    this.updateMealPeriods(() => {
      if (this.serverId != null) return
      this.setAllMealPeriods()
    })
  },
  methods: {
    locationMealPeriodConnectionRule (v) {
      if (!this.updatingMealPeriods) {
        if (v.every(l => !!this.selectedMealPeriodsMapByLocation[l.locationId])) return ''
        else return 'Each location selected must have at lease 1 corresponding Meal Period below'
      }
    },
    setAllLocations () {
      this.selectedLocations = []
      this.$nextTick(() => {
        this.updateMealPeriods(this.setAllMealPeriods)
      })
    },
    setAllMealPeriods () {
      this.selectedMealPeriods = this.mealPeriodsOptions.map(m => m.mealPeriodId)
    },
    toggleLocation (item, value) {
      let clone = Array.from(this.selectedLocations) // Can't push/splice to a computed property in this context
      if (value) clone.push(item.locationId)
      else {
        const spliceIndex = clone.findIndex(e => e.locationId === item.locationId)
        if (spliceIndex !== -1) clone.splice(spliceIndex, 1)
      }
      this.selectedLocations = clone
      // Now process changes to Meal Periods accordingly
      this.$nextTick(() => { // Give `selectedLocations` a tick to update 
        if (!clone?.length) {
          this.updateMealPeriods(() => this.selectedMealPeriods = Array.from(this.mealPeriodsOptions.map(m => m.mealPeriodId)))
        } else if (value) {
          if (clone.length === 1) 
            this.selectedMealPeriods = []
          this.$nextTick(() => {
            clone = new Set(this.selectedMealPeriods.map(m => m.mealPeriodId))
            this.updateMealPeriods(() => {
              for (let m of this.mealPeriodsOptions)
                if (m.locationId === item.locationId) clone.add(m.mealPeriodId)
              this.selectedMealPeriods = Array.from(clone)
            })
          })
        } else {
          clone = Array.from(this.selectedMealPeriods)
          this.updateMealPeriods(() => {
            clone = Array.from(clone).filter(m => m.locationId !== item.locationId)
            this.selectedMealPeriods = clone
          })
        }
      })
    },
    toggleMealPeriod (item, value) {
      let clone = Array.from(this.selectedMealPeriods)
      if (value) clone.push(item.mealPeriodId)
      else {
        const spliceIndex = this.selectedMealPeriods.findIndex(m => m.mealPeriodId === item.mealPeriodId)
        if (spliceIndex !== -1) clone.splice(spliceIndex, 1)
      }
      this.selectedMealPeriods = clone
    },
    updateMealPeriods (afterLoad) {
      let filter_methods = {}
      if (this.selectedLocations?.length) filter_methods.filterLocation = [this.selectedLocations?.map(l => l.locationId) ?? []]
      this.updatingMealPeriods = true
      this.$ftAPI().get(this.addResourceErrorHandler({
        command: 'diningMealPeriod',
        afterLoad: (r) => {
          this.mealPeriodsOptions = r?.map(e => {
            return {mealPeriodName: e.name, mealPeriodId: e.id, locationId: e.locationId}
          }) ?? []
          this.updatingMealPeriods = false
          setTimeout(() => afterLoad?.())
        },
        error: (e) => {
          this.updatingMealPeriods = false
          this.errors.push(e.message)
        },
        params: {filter_methods}
      }))
    },
  }
}
</script>